import React from 'react';
import { Package, Weight, Layers, AlertTriangle, ChevronDown, ChevronUp, Box, Eye, EyeOff } from 'lucide-react';
import type { PalletPreviewResponse, PalletPreviewDto, PalletLayout } from '@/types/loading-plan';
import { Pallet3DViewer } from '@/components/loading-plan/Pallet3DViewer';
import { AlgorithmDebugPanel } from '@/components/loading-plan/AlgorithmDebugPanel';
import { PALLET_3D_PREVIEW_ENABLED } from '@/constants/featureFlags';

// Cambiar a true cuando haya que depurar el algoritmo de paletización.
// Se mantiene el código del panel a propósito para reactivarlo rápido.
const SHOW_ALGORITHM_DEBUG_PANEL = true;

interface PalletCardProps {
  pallet: PalletPreviewDto;
  palletLayout: PalletLayout;
  /** Slot opcional para inyectar el editor de allocations dentro de la tarjeta expandida. */
  footerSlot?: React.ReactNode;
}

export function PalletCard({ pallet, palletLayout, footerSlot }: PalletCardProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [show3D, setShow3D] = React.useState(false);

  return (
    <div className="border border-green-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 hover:bg-green-50 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-green-600 shrink-0" />
          <span className="font-semibold text-sm text-green-900">
            Palet {pallet.sequenceNumber}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <span className="flex items-center gap-1">
            <Package className="h-3.5 w-3.5" />
            {pallet.totalBoxes} cajas
          </span>
          <span className="flex items-center gap-1">
            <Weight className="h-3.5 w-3.5" />
            {pallet.totalWeightKg.toFixed(1)} kg
          </span>
          <span className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {pallet.estimatedHeightCm.toFixed(0)} cm
          </span>
          {expanded
            ? <ChevronUp className="h-4 w-4 text-slate-400" />
            : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-green-100">
          {/* Productos */}
          <div className="mt-2 space-y-1 mb-3">
            {pallet.items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 text-sm"
              >
                <span className="font-medium text-slate-800 truncate mr-2">{item.productName}</span>
                <div className="flex gap-3 text-slate-500 shrink-0 text-xs">
                  <span>{item.numberOfBoxes} {item.packagingName || 'cajas'}</span>
                  <span>{item.weightKg.toFixed(1)} kg</span>
                </div>
              </div>
            ))}
          </div>

          {/* Botón 3D */}
          {PALLET_3D_PREVIEW_ENABLED && (pallet.layout?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => setShow3D(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >
              <Box className="h-3.5 w-3.5" />
              VER 3D
            </button>
          )}

          {footerSlot}
        </div>
      )}

      {show3D && (
        <Pallet3DViewer
          palletId={null}
          type="virtual"
          isOpen={show3D}
          onClose={() => setShow3D(false)}
          palletNumber={pallet.sequenceNumber}
          initialData={palletLayout}
        />
      )}
    </div>
  );
}

interface PalletPreviewPanelProps {
  preview: PalletPreviewResponse;
  /** Si true, oculta las tarjetas de palets (lista de contenido). Útil cuando se renderizan en otro sitio. */
  hidePalletCards?: boolean;
  /** Si true, oculta el resumen "X palets · Y cajas · Z kg" del cabecero. */
  hideSummary?: boolean;
}

export function PalletPreviewPanel({ preview, hidePalletCards, hideSummary }: PalletPreviewPanelProps) {
  const [selectedPalletIdx, setSelectedPalletIdx] = React.useState(0);
  // El visor 3D no se monta por defecto: hay equipos con pocos recursos que se
  // ahogan al cargarlo automáticamente. El operario lo abre on-demand desde el
  // botón "Ver 3D" si lo necesita; el hueco/contenedor se mantiene siempre.
  const [show3D, setShow3D] = React.useState(false);

  if (preview.pallets.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex items-center gap-2 mb-1 font-semibold">
          <AlertTriangle className="h-4 w-4" />
          Sin pallets calculados
        </div>
        {preview.warnings.map((w, i) => (
          <p key={i} className="text-xs">{w}</p>
        ))}
      </div>
    );
  }

  const selectedPallet = preview.pallets[selectedPalletIdx];
  const selectedLayout: PalletLayout | undefined = selectedPallet ? {
    length: preview.palletLengthCm,
    width: preview.palletWidthCm,
    baseHeight: preview.palletBaseHeightCm,
    palletModelUrl: preview.palletModelUrl,
    items: selectedPallet.layout ?? [],
    debugLayers: selectedPallet.debugLayers,
  } : undefined;

  return (
    <div className="space-y-3">
      {/* Summary */}
      {!hideSummary && (
        <div className="flex items-center gap-4 text-sm text-slate-600 bg-green-50 rounded-lg px-3 py-2 border border-green-100">
          <span className="font-semibold text-green-800">
            {preview.totalPallets} palet{preview.totalPallets !== 1 ? 's' : ''}
          </span>
          <span>·</span>
          <span>
            {preview.pallets.reduce((s, p) => s + p.totalBoxes, 0)} cajas totales
          </span>
          <span>·</span>
          <span>
            {preview.pallets.reduce((s, p) => s + p.totalWeightKg, 0).toFixed(1)} kg totales
          </span>
        </div>
      )}

      {/* Warnings */}
      {(preview.warnings.length > 0 || preview.linesWithoutPackaging.length > 0) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-0.5">
          {preview.linesWithoutPackaging.length > 0 && (
            <p>
              <span className="font-semibold">Sin envase asignado:</span>{' '}
              {preview.linesWithoutPackaging.join(', ')} (no incluidos en el cálculo)
            </p>
          )}
          {preview.warnings.map((w, i) => <p key={i}>{w}</p>)}
        </div>
      )}

      {/* Debug del algoritmo por pasadas. Oculto por defecto (SHOW_ALGORITHM_DEBUG_PANEL=false). */}
      {SHOW_ALGORITHM_DEBUG_PANEL && preview.debugTrace && preview.debugTrace.length > 0 && (
        <AlgorithmDebugPanel
          trace={preview.debugTrace}
          palletLengthCm={preview.palletLengthCm}
          palletWidthCm={preview.palletWidthCm}
          palletBaseHeightCm={preview.palletBaseHeightCm}
        />
      )}

      {/* 3D viewer inline — visible en desktop, bajo demanda para no asfixiar
          equipos modestos. Conservamos el hueco con la misma altura. */}
      {selectedLayout && (selectedLayout.items?.length ?? 0) > 0 && (
        <div className="hidden lg:block rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative" style={{ height: 500 }}>
          {show3D ? (
            <>
              <Pallet3DViewer
                palletId={null}
                type="virtual"
                isOpen={true}
                onClose={() => {}}
                palletNumber={selectedPallet.sequenceNumber}
                embedded={true}
                initialData={selectedLayout}
              />
              <button
                type="button"
                onClick={() => setShow3D(false)}
                className="absolute top-2 right-2 bg-white/90 backdrop-blur border border-slate-200 hover:bg-white rounded-md px-2.5 py-1 text-xs font-medium text-slate-700 inline-flex items-center gap-1.5 shadow-sm"
                title="Descargar el visor 3D"
              >
                <EyeOff size={13} /> Ocultar 3D
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShow3D(true)}
              className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-500 hover:bg-slate-200/60 transition-colors"
            >
              <Eye size={28} className="text-slate-400" />
              <span className="text-sm font-medium">Ver palet en 3D</span>
              <span className="text-xs text-slate-400">No se carga automáticamente para no ralentizar equipos modestos</span>
            </button>
          )}
        </div>
      )}

      {/* Pallet selector tabs (si hay más de 1) */}
      {preview.pallets.length > 1 && (
        <div className="flex gap-1">
          {preview.pallets.map((p, idx) => (
            <button
              key={p.sequenceNumber}
              type="button"
              onClick={() => setSelectedPalletIdx(idx)}
              className={`flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-colors ${
                idx === selectedPalletIdx
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Palet {p.sequenceNumber} · {p.totalBoxes} cajas
            </button>
          ))}
        </div>
      )}

      {/* Pallet cards */}
      {!hidePalletCards && (
        <div className="space-y-2">
          {preview.pallets.map(pallet => {
            const palletLayout: PalletLayout = {
              length: preview.palletLengthCm,
              width: preview.palletWidthCm,
              baseHeight: preview.palletBaseHeightCm,
              palletModelUrl: preview.palletModelUrl,
              items: pallet.layout ?? [],
              debugLayers: pallet.debugLayers,
            };
            return (
              <PalletCard key={pallet.sequenceNumber} pallet={pallet} palletLayout={palletLayout} />
            );
          })}
        </div>
      )}
    </div>
  );
}
